import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import H2 from '../Headings/H2'

const TableOfContent = ({ list, scrollOffset = 200 }) => {
  const [activeItem, setActiveItem] = useState('')

  // Helper: Scroll to element with offset
  const scrollToElementWithOffset = useCallback((targetId, offset = 200) => {
    const elementId = targetId.startsWith('#')
      ? targetId.substring(1)
      : targetId
    const element = document.getElementById(elementId)

    if (!element) {
      console.warn(`TableOfContent: Element "${elementId}" not found`)

      return false
    }

    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - offset

    // Skip if already at position (within 10px threshold)
    if (Math.abs(window.pageYOffset - offsetPosition) < 10) {
      return true
    }

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    })

    return true
  }, [])

  // Handle click on TOC links
  const handleClick = useCallback(
    (e) => {
      e.preventDefault()

      const targetHref = e.target.getAttribute('href')
      const offset = scrollOffset

      const scrollSuccess = scrollToElementWithOffset(targetHref, offset)

      if (scrollSuccess) {
        // Update URL without jumping
        if (window.history && window.history.pushState) {
          window.history.pushState(null, null, targetHref)
        } else {
          window.location.hash = targetHref
        }

        setActiveItem(targetHref)
      }
    },
    [scrollOffset, scrollToElementWithOffset]
  )

  // Handle initial hash on page load
  useEffect(() => {
    const hash = window.location.hash

    if (hash) {
      setActiveItem(hash)

      // Delay to ensure DOM is ready
      const timer = setTimeout(() => {
        const offset = scrollOffset
        scrollToElementWithOffset(hash, offset)
      }, 100)

      return () => clearTimeout(timer)
    } else if (list[0]?.href) {
      setActiveItem(list[0].href)
    }
  }, [list, scrollOffset, scrollToElementWithOffset])

  return (
    <div data-testid="table-of-content">
      <H2 text="Table of Contents" id="tableOfContents" />
      <ul id="table-of-contents" data-testid="table-of-contents">
        {list.map(({ value, href }, k) => {
          return value ? (
            <li key={`${value}-${k}`} data-testid="table-of-content-item">
              <a
                className={classNames(
                  { 'border-blue-550': href === activeItem },
                  `block border-l-4 py-2 pl-6 text-xl font-medium text-black-base`
                )}
                data-testid="table-of-content-link"
                href={`${href}`}
                id={`${href}-${k}`}
                onClick={handleClick}
              >
                {value}
              </a>
            </li>
          ) : null
        })}
      </ul>
    </div>
  )
}

TableOfContent.propTypes = {
  list: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    })
  ).isRequired,
  scrollOffset: PropTypes.number,
}

TableOfContent.defaultProps = {
  scrollOffset: 200,
}

export default TableOfContent
